<template>
	<v-form ref="form">
		<v-row>
			<v-col
				v-if="fieldAvailability.postalCode"
				cols="12"
				sm="6"
				style="padding: 12px !important;"
			>
				<v-text-field
					v-model:model-value="address.postalcode"
					:label="$gettext('Postal code')"
					:rules="[v => !fieldRequirement.postalCode || !!v || $gettext('Postal code is required'),
						v => validatePostcode(v)]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.postalCode : null"
					v-bind="$attrs"
					variant="outlined"
					@blur="autofill"
				>
					<template #append-inner>
						<slot
							name="append-postalcode"
							:address="address"
						>
							<v-icon
								v-if="appendOnValid && address.postalcode && address.postalcode.length > 0"
								color="primary"
								:icon="appendOnValid"
							/>
						</slot>
					</template>
				</v-text-field>
			</v-col>
			<v-col
				v-if="fieldAvailability.city"
				cols="12"
				sm="6"
				style="padding: 12px !important;"
			>
				<v-text-field
					ref="city"
					v-model:model-value="address.city"
					:label="$gettext('City')"
					:rules="[v => !fieldRequirement.city || !!v || $gettext('City is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.city : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.city && address.city.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
		</v-row>
		<v-row>
			<v-col
				v-if="fieldAvailability.street"
				cols="12"
				:lg="fieldAvailability.houseNumber || fieldAvailability.houseNumberSuffix ? 6 : 12"
			>
				<v-text-field
					ref="street"
					v-model:model-value="address.street"
					:label="$gettext('Street')"
					:rules="[v => !fieldRequirement.street || !!v || $gettext('Street Name is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.street : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.street && address.street.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
			<v-col
				v-if="fieldAvailability.houseNumber"
			>
				<v-text-field
					v-model:model-value="address.housenumber"
					:label="$gettext('Number')"
					:rules="[v => !fieldRequirement.houseNumber || !!v || $gettext('House Number is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.houseNumber : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.housenumber && address.housenumber.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
			<v-col
				v-if="fieldAvailability.houseNumberSuffix"
			>
				<v-text-field
					v-model:model-value="address.housenumberSuffix"
					:label="$gettext('Suffix')"
					:rules="[v => !fieldRequirement.houseNumberSuffix || !!v || $gettext('Suffix is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.houseNumberSuffix : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.housenumberSuffix && address.housenumberSuffix.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
		</v-row>
		<v-row>
			<v-col
				v-if="fieldAvailability.houseName"
				cols="12"
				:lg="fieldAvailability.doorCode || fieldAvailability.floor ? 6 : 12"
			>
				<v-text-field
					v-model:model-value="address.houseName"
					:label="$gettext('House Name')"
					:rules="[v => !fieldRequirement.houseName || !!v || $gettext('House Name is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.houseName : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.houseName && address.houseName.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
			<v-col
				v-if="fieldAvailability.doorCode"
			>
				<v-text-field
					v-model:model-value="address.doorCode"
					:label="$gettext('Door')"
					:rules="[v => !fieldRequirement.doorCode || !!v || $gettext('Door Code is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.doorCode : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.doorCode && address.doorCode.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
			<v-col
				v-if="fieldAvailability.floor"
			>
				<v-text-field
					v-model:model-value="address.floor"
					:label="$gettext('Floor')"
					:rules="[v => !fieldRequirement.floor || !!v || $gettext('Floor is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.floor : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.floor && address.floor.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
		</v-row>
		<v-row>
			<v-col
				v-if="fieldAvailability.administrativeArea"
			>
				<v-text-field
					v-model:model-value="address.administrativeArea"
					:label="$gettext('County')"
					:rules="[v => !fieldRequirement.administrativeArea || !!v || $gettext('County is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.administrativeArea : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.administrativeArea && address.administrativeArea.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
			<v-col
				v-if="fieldAvailability.district"
			>
				<v-text-field
					v-model:model-value="address.residentialArea"
					:label="$gettext('District')"
					:rules="[v => !fieldRequirement.district || !!v || $gettext('District is required')]"
					:placeholder="enablePlaceholders ? fieldPlaceholders.district : null"
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.district && address.district.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
		</v-row>
		<v-row>
			<v-col>
				<v-text-field
					v-model:model-value="address.country"
					:label="$gettext('Country')"
					hide-details
					disabled
					v-bind="$attrs"
					variant="outlined"
				>
					<template
						v-if="appendOnValid && address.country && address.country.length > 0"
						#append-inner
					>
						<v-icon
							color="primary"
							:icon="appendOnValid"
						/>
					</template>
				</v-text-field>
			</v-col>
		</v-row>
	</v-form>
</template>

<script>
import {
	axios,
} from '../../services/axios-service';
import {
	extend,
} from 'lodash';
import displayError from 'laravel-front-library/src/utils/displayError/displayError';

export default {
	name: 'AddressFields',
	props: {
		countryName: {
			required: true,
			type: String,
		},
		userId: {
			require: false,
			type: [
				Number, String,
			],
			default: () => null,
		},
		modelValue: {
			type: Object,
			default: () => null,
		},
		enablePlaceholders: {
			type: Boolean,
			require: false,
			default: false
		},
		enableAutofill: {
			type: Boolean,
			require: false,
			default: false
		},
		appendOnValid: {
			type: String,
			require: false,
			default: null
		}
	},

	emits: ['update:modelValue',],

	data () {
		return {
			fieldAvailability: {
				'name': false,
				'organization': false,
				'street': false,
				'district': false,
				'city': false,
				'postalCode': false,
				'sortingCode': false,
				'houseNumber': false,
				'houseNumberSuffix': false,
				'houseName': false,
				'doorCode': false,
				'floor': false,
				'administrativeArea': false
			},
			fieldRequirement: {
				'name': false,
				'organization': false,
				'street': false,
				'district': false,
				'city': false,
				'postalCode': false,
				'sortingCode': false,
				'houseNumber': false,
				'houseNumberSuffix': false,
				'houseName': false,
				'doorCode': false,
				'floor': false,
				'administrativeArea': false
			},
			fieldPlaceholders: {
				'name': '',
				'organization': '',
				'street': '',
				'district': '',
				'city': '',
				'postalCode': '',
				'sortingCode': '',
				'houseNumber': '',
				'houseNumberSuffix': '',
				'houseName': '',
				'doorCode': '',
				'floor': '',
				'administrativeArea': ''
			},
			address: {
				'street': '',
				'housenumber': '',
				'housenumberSuffix': '',
				'postalcode': '',
				'city': '',
				'doorCode': '',
				'floor': '',
				'administrativeArea': '',
				'houseName': '',
				'residentialArea': '',
				'country': this.countryName,
			},
			postalcodeCache: ''
		};
	},

	watch: {
		/**
		 * Watch
		 */
		address: {
			/**
			 * Update address in parent component
			 */
			handler () {
				this.$emit('update:modelValue', this.address);
			},
			deep: true
		},

		modelValue: {
			/**
			 * Update address when prop changes
			 */
			handler () {
				for (const key of Object.keys(this.modelValue)) {
					this.address[key] = this.modelValue[key];
				}
			},
			deep: true
		},

		countryName: {
			/**
			 * Update field availability when we are changing the countryname prop
			 */
			handler () { // watch it
				this.getFieldInfo();
			}
		}
	},

	mounted () {
		this.getFieldInfo();
		this.getPlaceholders();
		extend(this.address, this.modelValue);
		if (this.userId !== null) {
			this.getAddress();
		}
	},

	methods: {

		/**
		 * Validate the form
		 *
		 * @returns {*}
		 */
		async validateAddress () {
			const {
				valid
			} = this.$refs.form.validate();

			return valid;
		},

		/**
		 *
		 * @param {string} v
		 * @returns {boolean|string}
		 */
		validatePostcode (v) {
			if (this.address.country === 'Netherlands') {
				return (!this.fieldRequirement.postalCode || /^\d{4} ?[a-zA-Z]{2}$/.test(v) || this.$gettext('Please enter a valid postal code'));
			}
			return true;
		},

		/**
		 * If autofill is enabled then attempted to get city and street based on the supplied postalcode
		 */
		autofill () {
			if (this.enableAutofill && this.address.postalcode && this.postalcodeCache !== this.address.postalcode) {
				axios.get(`/api/v0/addresses/suggest?country=${this.countryName}&postalcode=${this.address.postalcode}`).then(({
					data
				}) => {
					this.address.city = data.settlement;
					this.address.street = data.street;
				}). finally(() => {
					this.$refs.city.validate();
					this.$refs.street.validate();
				});
			}
		},

		/**
		 * Get available fields from a specific country
		 */
		getFieldInfo () {
			axios.get(`/api/v0/addresses/fields?country=${this.countryName}`).then(({
				data: data
			}) => {
				this.fieldAvailability = data.supported;
				this.fieldRequirement = data.required;
			}).catch((error) => {
				displayError(this.$gettext('Something has gone wrong during data retrieval'));
				window.captureException(error);
			});
		},

		/**
		 * Get the placeholders for the fields from a specific country
		 */
		getPlaceholders () {
			axios.get(`/api/v0/addresses/placeholders?country=${this.countryName}`).then(({
				data: data
			}) => {
				this.fieldPlaceholders = data.placeholders;
			}).catch((error) => {
				displayError(this.$gettext('Something has gone wrong during data retrieval'));
				window.captureException(error);
			});

		},

		/**
		 * Get address
		 */
		getAddress () {
			axios.get(`/api/v0/useraddresses?user_id=${this.userId}`).then(({
				data: data
			}) => {
				if (data[0]) {
					this.address = data[0].address;
				}
				if (!this.address.country) {
					this.address.country = this.countryName;
				}
			}).catch((error) => {
				displayError(this.$gettext('Something has gone wrong during data retrieval'));
				window.captureException(error);
			});
		},
	},
};

</script>
